import {
  AdditionalCompensationsContractsHistoryFragment,
  ContractTaxonomyFragment,
} from 'generated/resources/graphql'
import { differenceWith, uniqBy } from 'lodash'

import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { Update } from 'modules/contracts/components/ContractHistory/ContractVersionUpdate'
import formatUpdate, {
  formatAdditionalCompensationUpdate,
  formatCustomFieldUpdate,
  formatNewAdditionalCompensationUpdate,
} from 'modules/contracts/lib/formatUpdate'
import type { LegalEntityContractData } from 'modules/contracts/lib/legalEntityContractDataContext'
import type { Compensations } from 'modules/contracts/store/collections/compensations'
import type Compensation from 'modules/contracts/store/models/Compensation'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type CustomFieldValuesCollection from 'modules/custom_resources/store/collections/customFields/values'
import type CustomFieldValue from 'modules/custom_resources/store/models/customFields/Value'
import policies from 'modules/payroll_bundle/store/collections/policies'

import contractVersionsPayrollPolicies from '../store/collections/contractVersionsPayrollPolicies'
import type ContractVersionRequest from '../store/models/ContractVersionRequest'

const IGNORED_KEYS = [
  'id',
  'effective_on',
  'created_by_name',
  'created_by_avatar',
  'created_at',
  'action_type',
  'status',
  'fr_jours_par_an',
  'es_cotization_group_id',
  'es_tariff_group_id',
  'es_tariff_group_name',
  'updated_at',
  'de_flat_rate_tax',
  'de_activity_type',
  'de_employment_type',
  'de_personal_key_group_id',
]

const DE_KEYS = ['de_contract_type_id']
const PT_KEYS = ['pt_contract_type_id']
const ES_KEYS = [
  'es_contract_type_id',
  'es_contract_observations',
  'es_education_level_id',
  'es_has_teleworking_contract',
  'es_job_description',
  'es_professional_category_id',
  'es_working_day_type_id',
]
const FR_KEYS = [
  'fr_coefficient',
  'fr_contract_type_id',
  'fr_employee_type',
  'fr_forfait_jours',
  'fr_jours_par_an',
  'fr_jours_par_an_cents',
  'fr_level_id',
  'fr_mutual_id',
  'fr_professional_category',
  'fr_professional_category_id',
  'fr_step_id',
  'fr_work_type_id',
]

const getIgnoredKeys = (country: string): string[] => {
  const ignoredKeys = IGNORED_KEYS

  if (!hasFeature('dev_time_tracking_workable_days_definition')) {
    ignoredKeys.push('bank_holiday_treatment', 'working_time_percentage_in_cents')
  }

  if (!hasFeature('dev_time_tracking_annualisation_daily_value')) {
    ignoredKeys.push('annual_working_time_distribution')
  }

  switch (country) {
    case 'es':
      return ignoredKeys.concat(FR_KEYS, DE_KEYS, PT_KEYS)
    case 'fr':
      return ignoredKeys.concat(ES_KEYS, DE_KEYS, PT_KEYS)
    case 'de':
      return ignoredKeys.concat(ES_KEYS, FR_KEYS, PT_KEYS)
    case 'pt':
      return ignoredKeys.concat(ES_KEYS, FR_KEYS, DE_KEYS)
    default:
      return ignoredKeys.concat(ES_KEYS, DE_KEYS, FR_KEYS, PT_KEYS)
  }
}

interface calculateAdditionalCompensationUpdatesOptions {
  currentContractVersion: ContractVersion | ContractVersionRequest
  previousContractVersion: ContractVersion
  additionalCompensations: Compensations
  locale: string
  taxonomies: ContractTaxonomyFragment[]
}
const calculateAdditionalCompensationUpdates = ({
  currentContractVersion,
  previousContractVersion,
  additionalCompensations,
  locale,
  taxonomies,
}: calculateAdditionalCompensationUpdatesOptions): Update[] => {
  const currentCompensations = additionalCompensations.filter(
    (compensation) => compensation.attributes.contract_version_id === currentContractVersion.id
  )
  const previousCompensations = additionalCompensations.filter(
    (compensation) => compensation.attributes.contract_version_id === previousContractVersion.id
  )

  const compensationsComparator = (c1: Compensation, c2: Compensation) => {
    return (
      c1.attributes.amount === c2.attributes.amount &&
      c1.attributes.contracts_taxonomy_id === c2.attributes.contracts_taxonomy_id &&
      c1.attributes.compensation_type === c2.attributes.compensation_type
    )
  }
  const findTaxonomy = (taxonomyId: number) => {
    return taxonomies.find((taxonomy) => taxonomy.id === taxonomyId)
  }

  const newCompensations = differenceWith(
    currentCompensations,
    previousCompensations,
    compensationsComparator
  ).map((compensation) => {
    const taxonomy = findTaxonomy(compensation.attributes.contracts_taxonomy_id)

    return formatAdditionalCompensationUpdate(
      'new_additional_compensation',
      compensation,
      currentContractVersion,
      locale,
      taxonomy
    )
  })

  const deletedCompensations = differenceWith(
    previousCompensations,
    currentCompensations,
    compensationsComparator
  ).map((compensation) => {
    const taxonomy = findTaxonomy(compensation.attributes.contracts_taxonomy_id)

    return formatAdditionalCompensationUpdate(
      'deleted_additional_compensation',
      compensation,
      currentContractVersion,
      locale,
      taxonomy
    )
  })

  return newCompensations.filter(Boolean).concat(deletedCompensations.filter(Boolean))
}

interface calculateNewAdditionalCompensationUpdatesOptions {
  currentContractVersion: ContractVersion | ContractVersionRequest
  previousContractVersion: ContractVersion
  additionalCompensations: AdditionalCompensationsContractsHistoryFragment[]
}
const calculateNewAdditionalCompensationUpdates = ({
  currentContractVersion,
  previousContractVersion,
  additionalCompensations,
}: calculateNewAdditionalCompensationUpdatesOptions): Update[] => {
  const currentCompensations = additionalCompensations.filter(
    (compensation) => compensation.contractVersionId === currentContractVersion.id
  )
  const previousCompensations = additionalCompensations.filter(
    (compensation) => compensation.contractVersionId === previousContractVersion.id
  )

  const compensationsComparator = (
    c1: AdditionalCompensationsContractsHistoryFragment,
    c2: AdditionalCompensationsContractsHistoryFragment
  ) => {
    return (
      c1.amountStrategy?.amount === c2.amountStrategy?.amount &&
      c1.payrollConceptId === c2.payrollConceptId
    )
  }

  const newCompensations = differenceWith(
    currentCompensations,
    previousCompensations,
    compensationsComparator
  ).map((compensation) => {
    return formatNewAdditionalCompensationUpdate('new_additional_compensation', compensation)
  })

  const deletedCompensations = differenceWith(
    previousCompensations,
    currentCompensations,
    compensationsComparator
  ).map((compensation) => {
    return formatNewAdditionalCompensationUpdate('deleted_additional_compensation', compensation)
  })

  return newCompensations.filter(Boolean).concat(deletedCompensations.filter(Boolean))
}

const calculateCustomFieldsUpdates = (
  currentContractVersion: ContractVersion | ContractVersionRequest,
  previousContractVersion: ContractVersion,
  customFieldValues: CustomFieldValuesCollection
): Update[] => {
  const currentCustomFieldValues = customFieldValues.filter(
    (customField) => customField.attributes.instance_id === currentContractVersion.id
  )
  const previousCustomFieldValues = customFieldValues.filter(
    (customField) => customField.attributes.instance_id === previousContractVersion.id
  )

  const customFieldsComparator = (cf1: CustomFieldValue, cf2: CustomFieldValue) => {
    return cf1.attributes.field_id === cf2.attributes.field_id
  }

  const newCustomFields = differenceWith(
    currentCustomFieldValues,
    previousCustomFieldValues,
    customFieldsComparator
  ).map((customField) => {
    return formatCustomFieldUpdate('new_custom_field', customField)
  })

  const updatedCustomFields: Update[] = []
  return previousCustomFieldValues
    .reduce((updatedCustomFields, previousCustomFieldValue) => {
      const currentCustomFieldValue = currentCustomFieldValues.find(
        (cf) => previousCustomFieldValue.attributes.field_id === cf.attributes.field_id
      )
      if (previousCustomFieldValue.attributes.value !== currentCustomFieldValue?.attributes.value) {
        const change = formatCustomFieldUpdate(
          previousCustomFieldValue.attributes.label,
          currentCustomFieldValue,
          previousCustomFieldValue
        )
        updatedCustomFields.push(change)
      }
      return updatedCustomFields
    }, updatedCustomFields)
    .concat(newCustomFields)
}

const calculateBasicUpdates = (
  currentContractVersion: ContractVersion | ContractVersionRequest,
  previousContractVersion: ContractVersion,
  legalEntityContractData: LegalEntityContractData
): Update[] => {
  const attributes = currentContractVersion.toJS()
  const previousAttributes = previousContractVersion.toJS()
  const ignoredKeys = getIgnoredKeys(currentContractVersion.attributes.country)

  const updates = Object.keys(attributes).reduce((memo: Update[], attr: string) => {
    if (!ignoredKeys.includes(attr)) {
      const currentAttribute = attributes[attr as keyof typeof attributes]
      const previousAttribute = previousAttributes[attr as keyof typeof previousAttributes]
      if (currentAttribute !== previousAttribute) {
        memo.push(
          formatUpdate(
            attr,
            legalEntityContractData,
            currentContractVersion,
            previousContractVersion
          )
        )
      }
    }

    return memo
  }, [])

  return uniqBy(updates, (update: Update) => update.key)
}

const calculatePayrollPolicyUpdates = (
  currentContractVersion: ContractVersion | ContractVersionRequest,
  previousContractVersion: ContractVersion
): Update[] => {
  const update: Update[] = []
  const NONE = i18n.t('contract_versions.navigator.changes.none')

  const currentContractPolicy = contractVersionsPayrollPolicies.find({
    contract_version_id: currentContractVersion.attributes.id,
  })
  const previousContractPolicy = contractVersionsPayrollPolicies.find({
    contract_version_id: previousContractVersion.attributes.id,
  })
  const currentPolicyId = currentContractPolicy?.attributes.payroll_policy_id
  const previousPolicyId = previousContractPolicy?.attributes.payroll_policy_id

  if (currentPolicyId != previousPolicyId) {
    const currentPolicy = currentPolicyId ? policies.get(currentPolicyId) : null
    const previousPolicy = previousPolicyId ? policies.get(previousPolicyId) : null
    update.push({
      key: 'payroll_policy',
      currentValue: currentPolicy?.attributes.name || NONE,
      previousValue: previousPolicy?.attributes.name || NONE,
    })
  }

  return update
}

interface calculateContractVersionUpdatesOptions {
  currentContractVersion: ContractVersion | ContractVersionRequest
  legalEntityContractData: LegalEntityContractData
  newAdditionalCompensations?: AdditionalCompensationsContractsHistoryFragment[]
  additionalCompensations: Compensations
  customFieldValues?: CustomFieldValuesCollection
  previousContractVersion?: ContractVersion | null
  locale: string
  taxonomies: ContractTaxonomyFragment[]
}
const calculateContractVersionUpdates = ({
  currentContractVersion,
  legalEntityContractData,
  additionalCompensations,
  newAdditionalCompensations,
  customFieldValues,
  previousContractVersion,
  locale,
  taxonomies,
}: calculateContractVersionUpdatesOptions): Update[] => {
  if (!previousContractVersion) return []

  const basicUpdates = calculateBasicUpdates(
    currentContractVersion,
    previousContractVersion,
    legalEntityContractData
  )
  const additionalCompensationUpdates = calculateAdditionalCompensationUpdates({
    currentContractVersion: currentContractVersion,
    previousContractVersion: previousContractVersion,
    additionalCompensations,
    locale: locale,
    taxonomies: taxonomies,
  })

  const newAdditionalCompensationUpdates = calculateNewAdditionalCompensationUpdates({
    currentContractVersion: currentContractVersion,
    previousContractVersion: previousContractVersion,
    additionalCompensations: newAdditionalCompensations ?? [],
  })

  const payrollPoliciesUpdates = calculatePayrollPolicyUpdates(
    currentContractVersion,
    previousContractVersion
  )

  const customFieldUpdates = customFieldValues
    ? calculateCustomFieldsUpdates(
        currentContractVersion,
        previousContractVersion,
        customFieldValues
      )
    : []

  return basicUpdates.concat(
    additionalCompensationUpdates,
    newAdditionalCompensationUpdates,
    customFieldUpdates,
    payrollPoliciesUpdates
  )
}

export default calculateContractVersionUpdates
